import {
  participantSchema,
  photoPeopleResponseSchema,
  photoResponseSchema,
  type Participant,
  type PhotoPeopleResponse,
  type PhotoResponse,
  type PhotoSubject,
  type SaveProfilePhotosRequest,
} from "@teamna/shared";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { queryKeys } from "./keys.ts";

/**
 * Profile photos (migration 0029): the Team photo tool and the single uploads on Desk.
 *
 * A photo shows next to a name on almost every screen, so a change refreshes every owner, staff
 * and member read, not one list. Photo URLs are signed and expire after some hours; a refetch brings
 * fresh ones.
 */

/**
 * Every query family that can carry a photo: the owner office, the setup and Home reads under
 * `/business/businesses/:id` (participants, team, families, placement; internal review F4.1),
 * the staff day, the member side and the session lists.
 */
export const PHOTO_QUERY_ROOTS = [["owner"], ["setup"], ["staff"], ["me"], ["businesses"], ["sessions"]] as const;

export function refreshEverywhere(queryClient: QueryClient): void {
  for (const prefix of PHOTO_QUERY_ROOTS) {
    void queryClient.invalidateQueries({ queryKey: prefix });
  }
}

/** One save: the photos, and the key that makes a retry of the same save replay its answer. */
export type SavePhotosInput = SaveProfilePhotosRequest & { idempotencyKey: string };

const photosPath = (businessId: string) => `/owner/businesses/${businessId}/photos`;

/** GET /owner/businesses/:id/photos/people */
export function usePhotoPeople(businessId: string | null): UseQueryResult<PhotoPeopleResponse> {
  return useQuery({
    queryKey: queryKeys.owner.photoPeople(businessId ?? "none"),
    queryFn: () => api.get(`${photosPath(businessId!)}/people`, photoPeopleResponseSchema),
    enabled: Boolean(businessId),
  });
}

/**
 * POST /owner/businesses/:id/photos. All or nothing. Keep the same `idempotencyKey` while the
 * photos are the same, so a retry after a lost answer does not save them, or tell the families,
 * twice.
 */
export function useSaveProfilePhotos(
  businessId: string,
): UseMutationResult<PhotoPeopleResponse, unknown, SavePhotosInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ idempotencyKey, ...input }: SavePhotosInput) =>
      api.post(photosPath(businessId), input, photoPeopleResponseSchema, { idempotencyKey }),
    onSuccess: (people) => {
      queryClient.setQueryData(queryKeys.owner.photoPeople(businessId), people);
      refreshEverywhere(queryClient);
    },
  });
}

/** DELETE /owner/businesses/:id/photos/:kind/:id */
export function useRemoveProfilePhoto(
  businessId: string,
): UseMutationResult<PhotoPeopleResponse, unknown, PhotoSubject> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subject: PhotoSubject) =>
      api.remove(`${photosPath(businessId)}/${subject.kind}/${subject.id}`, photoPeopleResponseSchema),
    onSuccess: (people) => {
      queryClient.setQueryData(queryKeys.owner.photoPeople(businessId), people);
      refreshEverywhere(queryClient);
    },
  });
}

/** PUT or DELETE /me/businesses/:id/photo: your own photo at one business. `null` removes it. */
export function useSetMyTeamPhoto(businessId: string): UseMutationResult<PhotoResponse, unknown, string | null> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jpeg: string | null) =>
      jpeg === null
        ? api.remove(`/me/businesses/${businessId}/photo`, photoResponseSchema)
        : api.put(`/me/businesses/${businessId}/photo`, { jpeg }, photoResponseSchema),
    onSuccess: () => refreshEverywhere(queryClient),
  });
}

/** PUT or DELETE /me/participants/:id/photo: the photo of one of your people. `null` removes it. */
export function useSetFamilyPhoto(participantId: string): UseMutationResult<PhotoResponse, unknown, string | null> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jpeg: string | null) =>
      jpeg === null
        ? api.remove(`/me/participants/${participantId}/photo`, photoResponseSchema)
        : api.put(`/me/participants/${participantId}/photo`, { jpeg }, photoResponseSchema),
    onSuccess: () => refreshEverywhere(queryClient),
  });
}

/** PUT /me/participants/:id/photo-consent */
export function useSetPhotoConsent(participantId: string): UseMutationResult<Participant, unknown, boolean> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (photosAllowed: boolean) =>
      api.put(`/me/participants/${participantId}/photo-consent`, { photosAllowed }, participantSchema),
    onSuccess: () => refreshEverywhere(queryClient),
  });
}
